import {Map} from "lucide-react";
import {useCallback, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Button, buttonVariants} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {Item, ItemContent, ItemTitle} from "@/components/ui/item";
import {
  Sidebar as SchSidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {cn} from "@/lib/utils";
import {useStoredCompany} from "@/src/modules/company/store";
import {usePropertyRoutes} from "@/src/modules/routing/hooks";
import {getNavigationPathBeforePropertyChange} from "@/src/modules/routing/lib/get-navigation-path-before-property-change";

import {Property} from "../../properties/types";
import {useIsMobile} from "../hooks";
import {userPropertyService} from "../services/user-property";
import {useStoredMe} from "../store/me";
import {useStoredUserProperty} from "../store/user-property";
import {APP_BRAND_NAME, LOGO_URL_DESKTOP} from "../utils/constants";
import {NavUser} from "./nav-user";

type SectionItemsProps = {
  path: string;
  isActive: boolean;
  icon?: React.ElementType;
  label: string;
};

export default function Navbar() {
  const {state, isMobile, openMobile} = useSidebar();
  const company = useStoredCompany();

  const isExpanded = state === "expanded";
  // Mobile sheet uses openMobile; desktop uses expanded sidebar state. Tailwind `md:` (768px)
  // must not drive header layout here — tablets 768–1023px are mobile per useIsMobile.
  const showSidebarHeaderChrome = isMobile ? openMobile : isExpanded;

  return (
    <>
      <SchSidebar
        collapsible="icon"
        variant="sidebar"
        className={cn(
          !isMobile &&
            "!top-[var(--app-desktop-header-height)] bottom-auto !h-[calc(100svh-var(--app-desktop-header-height))]",
        )}
      >
        <SidebarHeader
          className={cn(
            showSidebarHeaderChrome
              ? cn(
                  "px-4 pt-4 pb-6",
                  isMobile
                    ? "flex flex-col gap-2"
                    : "flex h-14 flex-row items-center gap-2 py-0",
                )
              : "px-2 md:h-0 md:min-h-0 md:overflow-hidden md:p-0",
          )}
        >
          <div
            className={cn(
              "mb-4 gap-2.5 md:hidden",
              isExpanded ? "flex items-start" : "block",
            )}
          >
            <img
              alt="logo"
              className={cn("size-[35px] shrink-0", isExpanded ? "m-0" : "mr-auto ml-auto")}
              src={LOGO_URL_DESKTOP}
            />
            <div
              className={cn(
                "min-w-0 flex-1 animate-out origin-left pt-0.5 transition-opacity delay-150",
                isExpanded ? "relative scale-100 opacity-100" : "scale-0 opacity-0",
              )}
            >
              <h1 className="line-clamp-2 text-[13px] leading-snug font-semibold">
                {company?.name ?? APP_BRAND_NAME}
              </h1>
              {company?.name ? (
                <p className="text-muted-foreground mt-0.5 truncate text-[10px] leading-none">
                  {APP_BRAND_NAME}
                </p>
              ) : null}
            </div>
          </div>

          {showSidebarHeaderChrome ? (
            <>
              <PropertySelector />
              <OpenInGoogleMapsButton />
            </>
          ) : null}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <MainRoutes />
          </SidebarGroup>

          <SidebarGroup>
            <ManagementRoutes />
          </SidebarGroup>

          <SidebarGroup>
            <SettingsRoutes />
          </SidebarGroup>

          <SidebarGroup>
            <SupportRoutes />
          </SidebarGroup>

          <SidebarGroup className="mt-auto px-4 py-6">
            <NavUser me={useStoredMe()} />
          </SidebarGroup>
        </SidebarContent>
      </SchSidebar>
      {!isMobile ? (
        <div
          className={cn(
            "absolute z-30 flex size-7 items-center justify-center rounded-full border border-solid bg-white",
            // Bottom edge of sidebar header — aligns with PageHeader border-b on the content column
            "top-[var(--app-desktop-header-height)]",
            isExpanded ? "left-(--sidebar-width)" : "left-(--sidebar-width-icon)",
            "-translate-x-1/2 -translate-y-1/2",
            "animate-out transition-[left]",
          )}
        >
          <SidebarTrigger className="rounded-full" />
        </div>
      ) : null}
    </>
  );
}

function parseCoordinates(gpsCoordinates: string): [number, number] | null {
  const parts = gpsCoordinates.trim().split(",");
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lat, lng];
}

function OpenInGoogleMapsButton() {
  const userProperty = useStoredUserProperty();
  const isMobile = useIsMobile();
  const {state, openMobile} = useSidebar();
  const isExpanded = state === "expanded";
  const showButton = isMobile ? openMobile : isExpanded;

  if (!isMobile || !showButton || !userProperty?.gps_coordinates?.trim()) return null;
  if (parseCoordinates(userProperty.gps_coordinates) === null) return null;

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(userProperty.gps_coordinates)}`;

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        buttonVariants({variant: "default", size: "default"}),
        "mt-2 w-full justify-start",
      )}
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        <Map className="size-4" />
      </span>
      Open in Google Maps
    </a>
  );
}

function PropertySelector() {
  const isMobile = useIsMobile();
  const {toggleSidebar} = useSidebar();
  const userProperty = useStoredUserProperty();
  const me = useStoredMe();
  const location = useLocation();
  const navigate = useNavigate();

  const properties = useMemo(
    () =>
      [...(me?.properties ?? [])].toSorted((a, b) =>
        a.name.localeCompare(b.name, undefined, {sensitivity: "base"}),
      ),
    [me?.properties],
  );

  const comboboxValue = useMemo(() => {
    if (!userProperty?.id || !properties.length) return null;
    // Value must be a reference from `items` — Base UI drops unknown values in a layout
    // effect; passing a persisted store object that is not in `items` loops when controlled.
    return (
      properties.find((p) => String(p.id) === String(userProperty.id)) ?? null
    );
  }, [properties, userProperty?.id]);

  const isPropertyEqual = useCallback(
    (item: Property, selected: Property | null) =>
      String(item.id) === String(selected?.id),
    [],
  );

  if (!me || !properties.length) return null;

  return (
    <ComboboxGeneric<Property>
      showClear={false}
      items={properties}
      className="w-full whitespace-nowrap"
      disabled={me.properties.length <= 1}
      preserveCase
      getItemKey={(item: Property) => item.id}
      getItemLabel={(item: Property) => item.name}
      isItemEqual={isPropertyEqual}
      renderItem={(option: Property) => (
        <Item size="sm" className="p-0">
          <ItemContent>
            <ItemTitle className="font-normal">{option.name}</ItemTitle>
          </ItemContent>
        </Item>
      )}
      value={comboboxValue}
      onChange={(value: Property | null, eventDetails) => {
        if (!value || eventDetails.reason === "none") return;
        if (userProperty && String(userProperty.id) === String(value.id)) return;

        const applyProperty = () => {
          userPropertyService.setUserProperty(value);
          if (isMobile) {
            toggleSidebar();
          }
        };

        const nextPath = getNavigationPathBeforePropertyChange({
          pathname: location.pathname,
          nextProperty: value,
          isAdmin: Boolean(me.isAdmin),
          isSeniorSupervisor: Boolean(me.isSeniorSupervisor),
        });

        if (nextPath == null) {
          applyProperty();
          return;
        }

        navigate(nextPath, {replace: true});
        window.setTimeout(applyProperty, 0);
      }}
    />
  );
}

function MainRoutes() {
  const {mainRoutes} = usePropertyRoutes();
  const location = useLocation();

  return (
    <SidebarMenu>
      {mainRoutes.map((route) => (
        <SectionItem
          key={route.path}
          path={route.path}
          isActive={
            location.pathname === route.path || location.pathname.startsWith(route.path + "/")
          }
          icon={route.icon}
          label={route.label as string}
        />
      ))}
    </SidebarMenu>
  );
}

function ManagementRoutes() {
  const {managementRoutes} = usePropertyRoutes();
  const location = useLocation();

  if (!managementRoutes.length) return null;

  return (
    <>
      <SidebarGroupLabel>Management</SidebarGroupLabel>
      <SidebarMenu>
        {managementRoutes.map((route) => (
          <SectionItem
            key={route.path}
            path={route.path}
            isActive={
              location.pathname === route.path || location.pathname.startsWith(route.path + "/")
            }
            icon={route.icon}
            label={route.label as string}
          />
        ))}
      </SidebarMenu>
    </>
  );
}

function SettingsRoutes() {
  const {settingsRoutes} = usePropertyRoutes();
  const location = useLocation();

  if (!settingsRoutes.length) return null;

  return (
    <>
      <SidebarGroupLabel>Admin panel</SidebarGroupLabel>
      <SidebarMenu>
        {settingsRoutes.map((route) => (
          <SectionItem
            key={route.path}
            path={route.path}
            isActive={
              location.pathname === route.path || location.pathname.startsWith(route.path + "/")
            }
            icon={route.icon}
            label={route.label as string}
          />
        ))}
      </SidebarMenu>
    </>
  );
}

function SupportRoutes() {
  const {supportRoutes} = usePropertyRoutes();
  const location = useLocation();

  if (!supportRoutes.length) return null;

  return (
    <>
      <SidebarGroupLabel>Support</SidebarGroupLabel>
      <SidebarMenu>
        {supportRoutes.map((route) => (
          <SectionItem
            key={route.path}
            path={route.path}
            isActive={
              location.pathname === route.path || location.pathname.startsWith(route.path + "/")
            }
            icon={route.icon}
            label={route.label as string}
          />
        ))}
      </SidebarMenu>
    </>
  );
}

function SectionItem({path, isActive, icon: Icon, label}: SectionItemsProps) {
  const navigate = useNavigate();
  const {isMobile, openMobile, toggleSidebar} = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={label}
        render={
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start p-2 text-sm font-normal",
              isActive && "bg-blue-200 font-semibold !text-blue-800",
            )}
            onClick={() => {
              if (isMobile && openMobile) {
                toggleSidebar();
              }
              navigate(path);
            }}
          />
        }
      >
        {Icon && (
          <span className="flex size-4 shrink-0 items-center justify-center text-blue-600">
            <Icon className="size-4" />
          </span>
        )}
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
